// --- Dependencies -------------------------------------------------------------------------------
import type { ReactNode } from "react";
// --- Our Modules --------------------------------------------------------------------------------
import useTranslation from "@/hooks/useTranslation";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

export type TyBarTab = { key: string; label: string };

type Props = {
    title?: string;
    addLabel?: string;
    onAdd?: () => void;
    addDisabled?: boolean;
    onSort?: () => void;
    onBack?: () => void;

    /** A screen's own views, at the start of the bar and underlined when active. */
    tabs?: TyBarTab[];
    activeTab?: string;
    onTab?: (key: string) => void;

    /**
     * The fields the bar turns into while filtering. Passing it is what reveals the filter button —
     * a screen with nothing to filter never grows one.
     */
    filterContent?: ReactNode;
    isFiltering?: boolean;
    onFilterOpen?: () => void;
    onFilterApply?: () => void;
    onFilterClear?: () => void;
    onFilterCancel?: () => void;
    /** Whether anything is actually narrowing the list right now — the button says so when it is. */
    hasFilters?: boolean;

    /** Screen-specific controls, dropped in beside the standard ones. */
    children?: ReactNode;
};

// --- Logic --------------------------------------------------------------------------------------

/**
 * The strip above a screen's content, and the one place its views and its actions live.
 *
 * It has two states. Normally it is the bar: a title or a row of tabs at the start, then whatever the
 * screen adds, then the filter button and the primary action at the far end. Pressing the filter
 * button turns the whole bar into the screen's filter fields rather than dropping a panel over the
 * results being filtered — the bar is already the widest strip on the screen.
 *
 * Nothing here is required. A screen that passes only a title gets exactly what it got before tabs
 * and filtering existed.
 */
export default function ActionsBar({
    title,
    addLabel,
    onAdd,
    addDisabled,
    onSort,
    onBack,
    tabs,
    activeTab,
    onTab,
    filterContent,
    isFiltering,
    onFilterOpen,
    onFilterApply,
    onFilterClear,
    onFilterCancel,
    hasFilters,
    children,
}: Props) {
    const t = useTranslation();
    const canFilter = !!filterContent && !!onFilterOpen;

    if (canFilter && isFiltering) {
        return (
            <div className="actions-bar actions-bar--filtering">
                <div className="actions-bar-fields">{filterContent}</div>

                <div className="actions-bar-ctas">
                    <button
                        type="button"
                        className="actions-bar-cta actions-bar-cta--clear"
                        onClick={onFilterClear}
                        title={t.actionsBar.clearFilters}
                    >
                        <img src="/icons/delete-icon.svg" alt="" width={16} height={16} />
                    </button>
                    <button type="button" className="actions-bar-cta actions-bar-cta--cancel" onClick={onFilterCancel} title={t.actionsBar.cancel}>
                        <img src="/icons/close-icon.svg" alt="" width={14} height={14} />
                    </button>
                    <button
                        type="button"
                        className="actions-bar-cta actions-bar-cta--apply"
                        onClick={onFilterApply}
                        title={t.actionsBar.applyFilters}
                    >
                        <img src="/icons/check-icon.svg" alt="" width={16} height={16} />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="actions-bar">
            {onBack && (
                <button type="button" className="actions-bar-back-btn" onClick={onBack}>
                    <img src="/icons/arrow-dark-icon.svg" alt="" width={20} height={20} />
                </button>
            )}

            {title && <p className="actions-bar-title">{title}</p>}

            {tabs && tabs.length > 0 && (
                <nav className="actions-bar-tabs">
                    {tabs.map((tab) => (
                        <button
                            key={tab.key}
                            type="button"
                            className="actions-bar-tab"
                            data-on={tab.key === activeTab}
                            onClick={() => onTab?.(tab.key)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </nav>
            )}

            {children}

            {/* Fills whatever is left, so the group after it ends up at the far edge. */}
            <span className="actions-bar-separator" />

            <div className="actions-bar-actions">
                {canFilter && (
                    <button
                        type="button"
                        className="actions-bar-filter-btn"
                        data-on={!!hasFilters}
                        onClick={onFilterOpen}
                        aria-label={t.actionsBar.filters}
                        title={t.actionsBar.filters}
                    >
                        <img src="/icons/filter-icon.svg" alt="" width={16} height={16} />
                    </button>
                )}

                {onSort && (
                    <button type="button" className="actions-bar-sort-btn" onClick={onSort}>
                        <img src="/icons/menu-icon.svg" alt="" width={16} height={16} />
                    </button>
                )}

                {onAdd && (
                    <button type="button" className="actions-bar-add-btn" onClick={onAdd} disabled={addDisabled}>
                        <img src="/icons/plus-icon.svg" alt="" width={16} height={16} />
                        <span className="actions-bar-add-label">{addLabel}</span>
                    </button>
                )}
            </div>
        </div>
    );
}
